/**
 * Loaders for the content formats Assets owns: generic JSON, the seed, the
 * content index and level manifests.
 *
 * The level loader resolves seed refs as part of loading, so everything
 * downstream (World, Inspector) sees plain values. An unresolved ref fails the
 * load (A-38) rather than producing a half-empty level.
 */
import type { AssetRef, AssetRegistry } from "./AssetRegistry";
import { INDEX_FORMAT_VERSION, MANIFEST_FORMAT_VERSION, SEED_FORMAT_VERSION } from "./content";
import type { ContentIndex, ContentSeed, LevelManifest } from "./content";
import { checkFormatVersion } from "./formatVersion";
import { upgradeManifest } from "./manifestUpgrades";
import { resolveRefs } from "./refs";

declare module "./AssetRegistry" {
  interface AssetTypeMap {
    json: unknown;
    seed: ContentSeed;
    index: ContentIndex;
    level: LevelManifest;
  }
}

export const SEED_REF = { kind: "seed", path: "content.seed.json" } as const satisfies AssetRef;
export const INDEX_REF = { kind: "index", path: "index.json" } as const satisfies AssetRef;

export function registerContentLoaders(registry: AssetRegistry): void {
  registry.registerLoader("json", (bytes, ref) => parseJson(bytes, ref.path));

  registry.registerLoader("seed", (bytes, ref) => {
    const doc = parseJson(bytes, ref.path);
    checkFormatVersion(doc, SEED_FORMAT_VERSION, ref.path);
    return doc as ContentSeed;
  });

  registry.registerLoader("index", (bytes, ref) => {
    const doc = parseJson(bytes, ref.path);
    checkFormatVersion(doc, INDEX_FORMAT_VERSION, ref.path);
    return doc as ContentIndex;
  });

  registry.registerLoader("level", async (bytes, ref, assets) => {
    const doc = parseJson(bytes, ref.path);
    checkFormatVersion(doc, MANIFEST_FORMAT_VERSION, ref.path);
    assertManifestShape(doc, ref.path);

    const seed = await assets.load(SEED_REF);
    return upgradeManifest(resolveRefs(doc, seed) as LevelManifest);
  });
}

function parseJson(bytes: Uint8Array, label: string): unknown {
  try {
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch (thrown) {
    const reason = thrown instanceof Error ? thrown.message : String(thrown);
    throw new Error(`${label}: invalid JSON (${reason})`, { cause: thrown });
  }
}

/**
 * The structural minimum a manifest must meet before the World looks at it.
 * Component-level validation happens later, against component schemas.
 */
function assertManifestShape(doc: unknown, label: string): asserts doc is LevelManifest {
  const manifest = doc as Partial<Record<keyof LevelManifest, unknown>>;

  for (const key of ["id", "kind", "name", "title"] as const) {
    if (typeof manifest[key] !== "string") throw new Error(`${label}: '${key}' must be a string`);
  }
  if (!Array.isArray(manifest.entities)) throw new Error(`${label}: 'entities' must be an array`);
}
